import React from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import {
  Page,
  PageHeader,
  DataTable,
  EmptyState,
  LoadingOrbit,
  Text,
  Button,
  Flex,
  TagBadge,
  badgeVariantToTagColor,
} from '@chatsdet/ui';
import { GithubLogo, ArrowLeft, ArrowRight } from '@phosphor-icons/react';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import { useRowLinkBuilder } from '../../lib/useRowLink';
import { safeHref } from '../../utils/safeHref';
import {
  usePrReviews,
  VERDICT_META,
  STATUS_META,
  TRIGGER_LABEL,
  REPO_FULL_NAME_RE,
} from '../../features/pr-reviews';
import type { PrReviewListItem } from '../../features/pr-reviews';

function VerdictCell({ review }: { review: PrReviewListItem }) {
  // No "inconclusive" verdict exists on this feature — a failed run leaves
  // verdict null. Render that as plain muted text, never a third pill.
  if (!review.verdict) {
    return (
      <Text size="sm" className="text-muted-foreground">
        —
      </Text>
    );
  }
  const meta = VERDICT_META[review.verdict];
  return <TagBadge name={meta.label} color={badgeVariantToTagColor(meta.variant)} hideDot />;
}

function PrCell({ review }: { review: PrReviewListItem }) {
  const valid = REPO_FULL_NAME_RE.test(review.repoFullName);
  const href = valid
    ? safeHref(`https://github.com/${review.repoFullName}/pull/${review.prNumber}`)
    : undefined;

  const content = (
    <Text size="sm" weight="medium" className="truncate">
      #{review.prNumber} {review.prTitle}
    </Text>
  );

  if (!href) {
    return <div className="min-w-0">{content}</div>;
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="min-w-0 block hover:underline"
      title={`Open #${review.prNumber} on GitHub`}
    >
      {content}
    </a>
  );
}

/**
 * AI PR Review — look-back list of AI-generated GitHub PR reviews. Reviews
 * are created by triggers (publish / `@bot review` mention), not from this
 * UI; this page and its detail page are read-only.
 */
export function PrReviews() {
  const navigate = useNavigate();
  const buildHref = useRowLinkBuilder();
  const search = useSearch({ from: '/pr-reviews' }) as { cursor?: string };
  const cursor = search.cursor;
  // Cursor tokens can't be derived in reverse, so "Back" replays a locally
  // held stack of prior cursors rather than round-tripping through the API.
  const [cursorHistory, setCursorHistory] = React.useState<string[]>([]);

  useBreadcrumbs([{ key: 'pr-reviews', label: 'PR Reviews' }]);

  const reviewsQuery = usePrReviews({ cursor });
  const items = reviewsQuery.data?.data ?? [];
  const nextCursor = reviewsQuery.data?.nextCursor ?? null;

  const goNext = () => {
    if (!nextCursor) return;
    setCursorHistory((prev) => [...prev, cursor ?? '']);
    navigate({ to: '/pr-reviews', search: { cursor: nextCursor } });
  };

  const goPrev = () => {
    setCursorHistory((prev) => {
      const next = [...prev];
      const previousCursor = next.pop();
      navigate({
        to: '/pr-reviews',
        search: previousCursor ? { cursor: previousCursor } : {},
      });
      return next;
    });
  };

  const columns: ColumnDef<StockFeatures, PrReviewListItem>[] = React.useMemo(
    () => [
      {
        id: 'repo',
        header: 'Repository',
        cell: ({ row }) => (
          <Text size="sm" className="truncate text-muted-foreground">
            {row.original.repoFullName}
          </Text>
        ),
        meta: { flex: 1.5 },
      },
      {
        id: 'pr',
        header: 'Pull Request',
        cell: ({ row }) => <PrCell review={row.original} />,
        meta: { flex: 3 },
      },
      {
        id: 'verdict',
        header: 'Verdict',
        cell: ({ row }) => <VerdictCell review={row.original} />,
        meta: { flex: 1 },
      },
      {
        id: 'trigger',
        header: 'Trigger',
        cell: ({ row }) => (
          <TagBadge name={TRIGGER_LABEL[row.original.trigger]} color="gray" hideDot />
        ),
        meta: { flex: 0.8 },
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const meta = STATUS_META[row.original.status];
          return (
            <TagBadge name={meta.label} color={badgeVariantToTagColor(meta.variant)} hideDot />
          );
        },
        meta: { flex: 0.8 },
      },
      {
        id: 'when',
        header: 'When',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground">
            {new Date(row.original.createdAt).toLocaleString()}
          </Text>
        ),
        meta: { flex: 1.2 },
      },
    ],
    []
  );

  return (
    <Page>
      <PageHeader
        icon={<GithubLogo size={24} />}
        title="PR Reviews"
        description="AI-generated production regression risk reviews posted to your GitHub pull requests."
      />

      <div className="mt-6">
        {reviewsQuery.isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingOrbit size="lg" />
          </div>
        ) : items.length > 0 ? (
          <>
            <DataTable
              columns={columns}
              data={items}
              aria-label="PR reviews table"
              getRowHref={(review) =>
                buildHref({ to: '/pr-reviews/$prReviewId', params: { prReviewId: review.id } })
              }
            />
            <Flex align="center" justify="end" gap="sm" className="mt-3">
              <Button
                variant="outline"
                size="sm"
                iconLeft={<ArrowLeft size={14} />}
                isDisabled={cursorHistory.length === 0 || reviewsQuery.isFetching}
                onPress={goPrev}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconRight={<ArrowRight size={14} />}
                isDisabled={!nextCursor || reviewsQuery.isFetching}
                onPress={goNext}
              >
                Next
              </Button>
            </Flex>
          </>
        ) : (
          <EmptyState
            icon={<GithubLogo size={48} />}
            title="No PR reviews yet"
            description="Once enabled, the AI reviewer posts a GitHub review whenever a PR is published or someone comments @bot review. Reviews will show up here as they run."
          />
        )}
      </div>
    </Page>
  );
}
